import React, { useEffect, useRef, useState } from "react";
import "../Profile/Profile.css";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api-2026.fikacreatives.co";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

async function geocodeZip(zip) {
  if (!MAPS_KEY) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(zip)}&components=postal_code:${encodeURIComponent(zip)}&key=${MAPS_KEY}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.status !== "OK" || !data.results?.length) return null;

    const components = data.results[0].address_components || [];
    const priority = [
      "neighborhood",
      "sublocality_level_1",
      "sublocality",
      "locality",
    ];
    for (const type of priority) {
      const comp = components.find((c) => c.types.includes(type));
      if (comp) return comp.long_name;
    }
    return null;
  } catch {
    return null;
  }
}

async function saveNeighborhood(neighborhood, person) {
  const res = await fetch(`${API_BASE}/api/settings/basic-details`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      first_name: person.first_name || "",
      last_name: person.last_name || "",
      email: person.email || "",
      birthday: person.birthday || null,
      city: person.city || "",
      zip_code: person.zip_code || "",
      neighborhood,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to save neighborhood");
  return data;
}

export default function NeighborhoodConfirmModal({ person, onClose, onSaved }) {
  const zip = String(person?.zip_code || "").trim();
  const currentNeighborhood = String(person?.neighborhood || "").trim();

  const [phase, setPhase] = useState("loading");
  const [geocoded, setGeocoded] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!zip) {
        setEditValue(currentNeighborhood);
        setPhase("edit");
        return;
      }

      const result = await geocodeZip(zip);
      if (cancelled) return;

      if (!result) {
        setEditValue(currentNeighborhood);
        setPhase("edit");
        return;
      }

      setGeocoded(result);
      setEditValue(result);
      setPhase("confirm");
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [zip, currentNeighborhood]);

  useEffect(() => {
    if (phase === "edit" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [phase]);

  async function handleConfirm(value) {
    setPhase("saving");
    setError("");
    try {
      await saveNeighborhood(value, person);
      setPhase("done");
      onSaved();
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setPhase(geocoded ? "confirm" : "edit");
    }
  }

  function handleClose() {
    sessionStorage.setItem("fika_nbhd_seen", "1");
    onClose();
  }

  return (
    <div className="pInviteOverlay" onClick={handleClose}>
      <div className="nbhdModal" onClick={(e) => e.stopPropagation()}>
        <button className="pInviteClose" onClick={handleClose} aria-label="Close">
          ✕
        </button>

        {phase === "loading" && (
          <div className="nbhdBody">
            <div className="nbhdPin">📍</div>
            <h2 className="nbhdTitle">Looking up your neighborhood…</h2>
            <p className="nbhdSub">Checking ZIP code {zip}</p>
          </div>
        )}

        {phase === "confirm" && (
          <div className="nbhdBody">
            <div className="nbhdPin">📍</div>
            <h2 className="nbhdTitle">Is this your neighborhood?</h2>
            <p className="nbhdSub">
              Based on your ZIP code <strong>{zip}</strong>, we think you're in:
            </p>
            <div className="nbhdDetected">{geocoded}</div>
            {error && <p className="nbhdError">{error}</p>}
            <div className="nbhdActions">
              <button
                className="pInviteCta"
                onClick={() => handleConfirm(geocoded)}
              >
                Yes, that's my neighborhood
              </button>
              <button
                className="nbhdChangeBtn"
                onClick={() => {
                  setEditValue(geocoded || "");
                  setPhase("edit");
                }}
              >
                Change it
              </button>
            </div>
          </div>
        )}

        {phase === "edit" && (
          <div className="nbhdBody">
            <div className="nbhdPin">📍</div>
            <h2 className="nbhdTitle">What's your neighborhood?</h2>
            <p className="nbhdSub">
              Enter your neighborhood so we can connect you with creatives nearby.
            </p>
            <input
              ref={inputRef}
              className="nbhdInput"
              type="text"
              placeholder="e.g. Silver Lake, Williamsburg, Midtown"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && editValue.trim()) handleConfirm(editValue.trim());
              }}
              maxLength={120}
            />
            {error && <p className="nbhdError">{error}</p>}
            <div className="nbhdActions">
              <button
                className="pInviteCta"
                onClick={() => handleConfirm(editValue.trim())}
                disabled={!editValue.trim()}
              >
                Save neighborhood
              </button>
              {geocoded && (
                <button
                  className="nbhdChangeBtn"
                  onClick={() => setPhase("confirm")}
                >
                  Back
                </button>
              )}
            </div>
          </div>
        )}

        {phase === "saving" && (
          <div className="nbhdBody">
            <div className="nbhdPin">📍</div>
            <h2 className="nbhdTitle">Saving…</h2>
          </div>
        )}

        {phase === "done" && (
          <div className="nbhdBody">
            <div className="nbhdPin nbhdPinDone">✓</div>
            <h2 className="nbhdTitle">Neighborhood saved!</h2>
            <p className="nbhdSub">
              We'll use this to personalize your Fika matches.
            </p>
            <div className="nbhdActions">
              <button className="pInviteCta" onClick={handleClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
